import { useEffect, useState } from 'react';
import { isValidYoutubeVideoId } from '../utils/listingVideoUtils';

/**
 * Orientación de un video YouTube (horizontal | vertical), solo Front.
 * 1) URL /shorts/ → vertical (sync, sin red).
 * 2) Si no: oEmbed público de YouTube (CORS OK) y height > width → vertical.
 * Cache en memoria + sessionStorage por video id. Si falla o mientras
 * carga → horizontal (comportamiento previo). No se persiste en la API.
 */
export const YT_ORIENTATION = { HORIZONTAL: 'horizontal', VERTICAL: 'vertical' };
// v2: la v1 consultaba oEmbed con watch?v= (siempre 16:9) y dejó
// 'horizontal' cacheado para shorts en sessionStorage. Cambiar la key lo invalida.
export const STORAGE_PREFIX = 'yt_orientation:v2:';
const memoryCache = new Map();
const pending = new Map();

export const isYoutubeShortsUrl = (url) => {
  try {
    const parsed = new URL(String(url || '').trim());
    const host = parsed.hostname.replace(/^www\./, '');
    return (host === 'youtube.com' || host === 'm.youtube.com')
      && parsed.pathname.startsWith('/shorts/');
  } catch {
    return false;
  }
};

const readStorage = (id) => {
  try {
    const value = window.sessionStorage.getItem(STORAGE_PREFIX + id);
    return value === YT_ORIENTATION.VERTICAL || value === YT_ORIENTATION.HORIZONTAL ? value : null;
  } catch {
    return null;
  }
};

const writeCache = (id, orientation) => {
  memoryCache.set(id, orientation);
  try {
    window.sessionStorage.setItem(STORAGE_PREFIX + id, orientation);
  } catch {
    /* sessionStorage no disponible */
  }
};

/** Orientación conocida sin red (URL o cache) o null. */
export const getKnownYoutubeOrientation = (video) => {
  const id = video?.youtube_video_id;
  if (!isValidYoutubeVideoId(id)) {
    return isYoutubeShortsUrl(video?.youtube_url) ? YT_ORIENTATION.VERTICAL : YT_ORIENTATION.HORIZONTAL;
  }
  if (isYoutubeShortsUrl(video?.youtube_url)) {
    // Se cachea por id: otra vista del mismo video (share, otra ficha) que
    // reciba otra forma de URL lo resuelve sin esperar oEmbed.
    if (memoryCache.get(id) !== YT_ORIENTATION.VERTICAL) writeCache(id, YT_ORIENTATION.VERTICAL);
    return YT_ORIENTATION.VERTICAL;
  }
  if (memoryCache.has(id)) return memoryCache.get(id);
  const stored = readStorage(id);
  if (stored) memoryCache.set(id, stored);
  return stored;
};

export const fetchYoutubeOrientation = (videoId) => {
  if (pending.has(videoId)) return pending.get(videoId);
  // Ojo: oEmbed con watch?v= devuelve siempre 16:9 (200x113) aunque sea un
  // short; con la forma /shorts/ID devuelve las proporciones reales
  // (vertical 113x200, horizontal 200x113). Verificado con curl 2026-10-16.
  const watchUrl = `https://www.youtube.com/shorts/${videoId}`;
  const promise = fetch(
    `https://www.youtube.com/oembed?url=${encodeURIComponent(watchUrl)}&format=json`,
  )
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`oembed ${res.status}`))))
    .then((data) => {
      const width = Number(data?.width);
      const height = Number(data?.height);
      const orientation = width > 0 && height > width
        ? YT_ORIENTATION.VERTICAL
        : YT_ORIENTATION.HORIZONTAL;
      writeCache(videoId, orientation);
      return orientation;
    })
    // Falla: horizontal sin cachear en storage (se reintenta en otra sesión).
    .catch(() => {
      memoryCache.set(videoId, YT_ORIENTATION.HORIZONTAL);
      return YT_ORIENTATION.HORIZONTAL;
    })
    .finally(() => pending.delete(videoId));
  pending.set(videoId, promise);
  return promise;
};

/** Solo para tests. */
export const __resetYoutubeOrientationCache = () => {
  memoryCache.clear();
  pending.clear();
};

export default function useYoutubeOrientation(video) {
  const id = video?.youtube_video_id;
  const url = video?.youtube_url;
  const [orientation, setOrientation] = useState(
    () => getKnownYoutubeOrientation(video) || YT_ORIENTATION.HORIZONTAL,
  );

  useEffect(() => {
    let active = true;
    const known = getKnownYoutubeOrientation({ youtube_video_id: id, youtube_url: url });
    if (known) {
      setOrientation(known);
      return undefined;
    }
    setOrientation(YT_ORIENTATION.HORIZONTAL);
    fetchYoutubeOrientation(id).then((next) => {
      if (active) setOrientation(next);
    });
    return () => {
      active = false;
    };
  }, [id, url]);

  return orientation;
}

/**
 * Orientaciones de una lista de videos (mismo orden). Para layouts de fila
 * que necesitan conocerlas todas (ListingVideoEmbeds). Mientras detecta →
 * horizontal.
 */
export function useYoutubeOrientations(videos) {
  const list = Array.isArray(videos) ? videos : [];
  const key = list.map((v) => `${v?.youtube_video_id || ''}|${v?.youtube_url || ''}`).join(',');
  const [resolved, setResolved] = useState({});

  useEffect(() => {
    let active = true;
    list.forEach((video) => {
      const id = video?.youtube_video_id;
      if (getKnownYoutubeOrientation(video) || !isValidYoutubeVideoId(id)) return;
      fetchYoutubeOrientation(id).then((next) => {
        if (active) setResolved((prev) => (prev[id] === next ? prev : { ...prev, [id]: next }));
      });
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return list.map((video) => (
    getKnownYoutubeOrientation(video)
    || resolved[video?.youtube_video_id]
    || YT_ORIENTATION.HORIZONTAL
  ));
}
